import { z } from "zod";
import { Language, UserRole, ReviewStatus, Gender, DepartmentPosition, EpisodeStage } from "./enums";

// ─── Auth ────────────────────────────────────────────────────────
export const MagicLinkRequestSchema = z.object({
  email: z.string().email(),
});

// ─── Users ───────────────────────────────────────────────────────
export const CreateUserSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  role: z.nativeEnum(UserRole).default(UserRole.Doctor),
  specialityId: z.string().uuid().optional().nullable(),
  departmentId: z.string().uuid().optional().nullable(),
  departmentPosition: z.nativeEnum(DepartmentPosition).optional().nullable(),
});

export const UpdateUserSchema = CreateUserSchema.partial();

// ─── Patients ────────────────────────────────────────────────────
export const CreatePatientSchema = z.object({
  name: z.string().min(1),
  personalNumber: z.string().min(1),
  gender: z.nativeEnum(Gender),
  phone: z.string().optional(),
  email: z.string().email().optional(),
  dateOfBirth: z.string().optional(),
});

export const UpdatePatientSchema = CreatePatientSchema.partial();

export const SharePatientSchema = z.object({
  doctorId: z.string().min(1),
});

// ─── Template fields ─────────────────────────────────────────────
export const I18nStringSchema = z.object({
  en: z.string(),
  ru: z.string(),
  kz: z.string(),
});

// A piece of text that can be either a plain string (legacy, single-language)
// or a localized map of { <langCode>: string }. Using an open record keeps it
// CMS-ready and extendable to new languages without a schema/column change.
export const LocalizedTextSchema = z.union([
  z.string(),
  z.record(z.string(), z.string()),
]);

export const TemplateFieldSchema = z.object({
  key: z.string().min(1),
  // label/options/normalValue accept a plain string (existing templates) or a
  // localized { en, ru, kz, ... } object (new templates). Backward-compatible.
  label: LocalizedTextSchema,
  bilateral: z.boolean(),
  importance: z.enum(["blocking", "normal", "extra"]),
  type: z.enum(["select", "text", "measurements"]),
  options: z.array(LocalizedTextSchema).optional(),
  normalValue: LocalizedTextSchema.nullable().optional(),
  // Which section of the report structure this answer belongs to. Optional:
  // unmapped fields are handed to the LLM which places them into the most
  // fitting section, so existing templates keep working untouched.
  sectionKey: z.string().optional().nullable(),
});

// Snapshot of the template fields stored on a report at generation time, so the
// report renders from its own copy and is unaffected by later template edits.
export const TemplateSnapshotSchema = z.object({
  version: z.number(),
  fields: z.array(TemplateFieldSchema),
});

// ─── Departments ─────────────────────────────────────────────────
export const CreateDepartmentSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  parentId: z.string().uuid().optional().nullable(),
});

export const UpdateDepartmentSchema = CreateDepartmentSchema.partial();

// ─── Specialities ─────────────────────────────────────────────────
export const CreateSpecialitySchema = z.object({
  name: z.string().min(1),
  nameRu: z.string().optional(),
  nameKz: z.string().optional(),
});

export const UpdateSpecialitySchema = CreateSpecialitySchema.partial();

// ─── Report Templates ─────────────────────────────────────────────
export const MOH_FORM_IDS = ["001u", "052u", "uzi-prenatal", "echokg"] as const;
export type MohFormId = (typeof MOH_FORM_IDS)[number];

export const MOH_FORM_LABELS: Record<MohFormId, string> = {
  "001u": "Форма №001/у — Медицинская карта стационарного пациента",
  "052u": "Форма №052/у — Медицинская карта амбулаторного пациента",
  "uzi-prenatal": "Стандарт пренатального УЗИ скрининга (№ДСМ-54/2022)",
  "echokg": "Стандарт кардиологической помощи (ЭХОКГ)",
};

export const CreateReportTemplateSchema = z.object({
  specialityId: z.string().uuid(),
  name: z.string().min(1),
  nameRu: z.string().optional(),
  nameKz: z.string().optional(),
  fields: z.array(TemplateFieldSchema).min(1),
  llmPrompt: z.string().optional(),
  isActive: z.boolean().default(true),
  isGeneral: z.boolean().default(false),
  mohFormId: z.enum(MOH_FORM_IDS).nullable().optional(),
  reportStructureId: z.string().uuid().nullable().optional(),
});

export const UpdateReportTemplateSchema = z.object({
  name: z.string().min(1).optional(),
  nameRu: z.string().nullable().optional(),
  nameKz: z.string().nullable().optional(),
  fields: z.array(TemplateFieldSchema).min(1).optional(),
  llmPrompt: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  isGeneral: z.boolean().optional(),
  mohFormId: z.enum(MOH_FORM_IDS).nullable().optional(),
  reportStructureId: z.string().uuid().nullable().optional(),
});

// ─── Report structures ───────────────────────────────────────────
// A report structure describes the *document*: which sections it has, in which
// order, and what belongs in each. It is deliberately separate from the
// template (the questionnaire): many templates map onto one structure
// (many-to-one), which is the answer to "one structure for all templates or
// one per template?" — neither, it is linked.
export const ReportSectionSchema = z.object({
  key: z.string().min(1),
  title: LocalizedTextSchema,
  order: z.number().int(),
  // "narrative" — coherent paragraph; "list" — one line per finding.
  render: z.enum(["narrative", "list"]).default("narrative"),
  // Free-text instruction telling the writer what belongs in this section.
  hint: LocalizedTextSchema.nullable().optional(),
  // Verbatim wording of an official form (MoH form 213/у and friends), with
  // gaps for the measurements and a list of alternative conclusions to pick
  // from. When set, the writer fills this text in instead of composing a
  // paragraph: these documents are prescribed forms, so the wording is not
  // the model's to choose. Unset → free narrative, as before.
  boilerplate: LocalizedTextSchema.nullable().optional(),
});

export const CreateReportStructureSchema = z.object({
  name: z.string().min(1),
  nameRu: z.string().optional().nullable(),
  nameKz: z.string().optional().nullable(),
  mohFormId: z.enum(MOH_FORM_IDS).nullable().optional(),
  sections: z.array(ReportSectionSchema).min(1),
  isActive: z.boolean().default(true),
  isDefault: z.boolean().default(false),
});

export const UpdateReportStructureSchema = CreateReportStructureSchema.partial();

// Snapshot of the structure stored on a report at generation time — same
// mechanism as TemplateSnapshot, so later structure edits never alter an
// already generated document.
export const ReportStructureSnapshotSchema = z.object({
  id: z.string().uuid().nullable().optional(),
  version: z.number(),
  sections: z.array(ReportSectionSchema),
});

// ─── Consultations ───────────────────────────────────────────────
export const CreateConsultationSchema = z.object({
  patientName: z.string().min(1),
  patientId: z.string().uuid().optional(),
  templateId: z.string().uuid(),
  language: z.nativeEnum(Language),
});

// ─── Transcription ───────────────────────────────────────────────
export const AddTranscriptionSchema = z.object({
  text: z.string().min(1),
  // Idempotency key. A client that retries an upload after a lost response sends
  // the same key, and the server returns the row it already stored instead of
  // appending the same text twice. See UPLOAD_MAX_ATTEMPTS in constants.ts.
  clientRequestId: z.string().min(1).max(128).optional(),
});

// ─── Report patch (inline editing + review) ──────────────────────
export const PatchReportSchema = z.object({
  editedData: z.record(z.unknown()).optional(),
  reviewStatus: z.nativeEnum(ReviewStatus).optional(),
  generalEditedData: z.record(z.unknown()).optional(),
  doctorNotes: z.string().optional(),
  // Per-section overrides of the generated narrative. The structured answers
  // stay the source of truth — this only overrides the rendered wording.
  narrativeEditedData: z.record(z.string()).optional(),
});

// ─── LLM Service (legacy) ────────────────────────────────────────
export const SummarizeRequestSchema = z.object({
  text: z.string().min(1),
  language: z.nativeEnum(Language),
  systemPrompt: z.string().optional(),
});

// ─── LLM Service (dynamic) ───────────────────────────────────────
export const DynamicSummarizeRequestSchema = z.object({
  text: z.string().min(1),
  fields: z.array(TemplateFieldSchema),
  language: z.nativeEnum(Language),
  llmPrompt: z.string().optional(),
  systemPrompt: z.string().optional(),
  specialityName: z.string().optional(),
});

export const TranslateRequestSchema = z.object({
  text: z.string().min(1),
  targetLanguage: z.nativeEnum(Language),
});

// ─── Medical Drugs ────────────────────────────────────────────────
export const CreateMedicalDrugSchema = z.object({
  name: z.string().min(1),
  nameKz: z.string().optional(),
  inn: z.string().min(1),
  manufacturer: z.string().optional(),
});

export const UpdateMedicalDrugSchema = CreateMedicalDrugSchema.partial();

// ─── Prompt Configs ───────────────────────────────────────────────
export const PROMPT_CONFIG_KEYS = ["base_instruction", "template_base_instruction", "transcribe_system"] as const;
export type PromptConfigKey = (typeof PROMPT_CONFIG_KEYS)[number];

export const PatchPromptConfigSchema = z.object({
  content: z.string().min(1),
});

// ─── LLM Service (KZ-SOAP general) ──────────────────────────────
export const KzSoapRequestSchema = z.object({
  text: z.string().min(1),
  language: z.nativeEnum(Language),
  systemPrompt: z.string().optional(),
  specialityName: z.string().optional(),
});

// ─── LLM Service (narrative report) ─────────────────────────────
// Turns already-extracted structured answers into the report document.
//
// The transcript comes along as *context for wording only* (Trello #134): the
// answers remain the only permitted source of facts, and the prompt forbids
// reporting anything the answers do not contain. Sections with `boilerplate`
// narrow this further — the model fills gaps in a fixed form.
export const NarrateReportRequestSchema = z.object({
  language: z.nativeEnum(Language),
  sections: z.array(ReportSectionSchema).min(1),
  // Consultation transcript. Optional: a report can still be rendered without
  // it, and the writer must produce the same facts either way.
  transcript: z.string().optional(),
  // Answers already pinned to a section via TemplateField.sectionKey.
  mapped: z.record(z.array(z.object({ label: z.string(), value: z.string() }))),
  // Answers from fields without a sectionKey — the model decides where each fits.
  unmapped: z.array(z.object({ label: z.string(), value: z.string() })).default([]),
  systemPrompt: z.string().optional(),
  specialityName: z.string().optional(),
});

// ─── Episodes ─────────────────────────────────────────────────────
export const CreateEpisodeSchema = z.object({
  patientId: z.string().uuid(),
  title: z.string().min(1),
  description: z.string().optional().nullable(),
  stage: z.nativeEnum(EpisodeStage).default(EpisodeStage.PrimaryExamination),
});

export const UpdateEpisodeSchema = z.object({
  title: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  stage: z.nativeEnum(EpisodeStage).optional(),
});

export const AssignEpisodeSchema = z.object({
  episodeId: z.string().uuid().nullable(),
});

// ─── Patient Diaries ──────────────────────────────────────────────
export const CreatePatientDiarySchema = z.object({
  title: z.string().min(1),
  content: z.string().min(1),
});

export const UpdatePatientDiarySchema = z.object({
  title: z.string().min(1).optional(),
  content: z.string().min(1).optional(),
});

// ─── Patient Medical Profile ──────────────────────────────────────
export const MedicalProfileItemSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1),
  notes: z.string().optional().nullable(),
});

export const UpsertMedicalProfileSchema = z.object({
  allergies: z.array(MedicalProfileItemSchema).optional(),
  chronicConditions: z.array(MedicalProfileItemSchema).optional(),
  surgicalHistory: z.array(MedicalProfileItemSchema).optional(),
});

// ─── Report linking ───────────────────────────────────────────────
export const LinkReportSchema = z.object({
  linkedReportId: z.string().uuid().nullable(),
});

// ─── Merge anonymous episode ──────────────────────────────────────
export const MergeAnonymousPatientSchema = z.object({
  targetPatientId: z.string().uuid(),
});

// ─── Primary visit ────────────────────────────────────────────────

/** Where one answer came from — see FieldSource in primary-visit.ts. */
export const FieldSourceSchema = z.enum([
  "transcript",
  "default",
  "protocol",
  "doctor",
]);

/** `{ [answerKey]: source }`. Absent key means the answer has no provenance yet. */
export const FieldSourcesSchema = z.record(z.string(), FieldSourceSchema);

/**
 * A diagnosis the model proposes. It is a suggestion and nothing else: it never
 * lands in `diagnosisMain` on its own — the doctor picks one, or types their own.
 */
export const DiagnosisCandidateSchema = z.object({
  name: z.string().min(1),
  nameKz: z.string().nullable().optional(),
  icd10: z.string().nullable().optional(),
  /** Clinical protocol backing this candidate, when one was found. */
  protocolId: z.string().uuid().nullable().optional(),
  /** What in the consultation points at it — shown to the doctor, not stored in the report. */
  rationale: z.string().nullable().optional(),
});

/**
 * Which parts of the report the doctor has signed off. Anything auto-filled
 * (`default`/`protocol`) has to appear here before the report can be submitted.
 */
export const ReportConfirmationSchema = z.object({
  confirmedById: z.string().uuid(),
  confirmedAt: z.string(),
});
export const ReportConfirmationsSchema = z.record(
  z.string(),
  ReportConfirmationSchema,
);

/** Body of POST /consultations/:id/report/confirm. */
export const ConfirmReportSectionsSchema = z.object({
  /** Section keys the doctor is confirming, e.g. ["objectiveStatus", "plan"]. */
  sections: z.array(z.string().min(1)).min(1),
});

/** Body of POST /consultations/:id/report/diagnosis — the doctor's decision. */
export const SetDiagnosisSchema = z.object({
  diagnosisMain: z.string().min(1),
  diagnosisIcd10: z.string().nullable().optional(),
  diagnosisConcomitant: z.string().nullable().optional(),
  diagnosisComplications: z.string().nullable().optional(),
  /**
   * Protocol to take the examination and treatment plan from. Omitted keeps the
   * plan as it stands; a new one refills both plan answers, marked `protocol`
   * and unconfirmed again — the doctor confirms the new plan, not the old one.
   */
  protocolId: z.string().uuid().nullable().optional(),
});

/** LLM service: POST /v1/extract-primary-visit. */
export const PrimaryVisitExtractRequestSchema = z.object({
  text: z.string().min(1),
  language: z.nativeEnum(Language),
  systemPrompt: z.string().optional(),
  /** Diagnosis names known to the protocol base, offered to the model to match against. */
  knownDiagnoses: z.array(z.string()).optional(),
  /**
   * Age in years and sex, when the patient record has them. Kazakhstan files
   * separate protocols for children and adults, so without the age the model
   * has offered a paediatric protocol for an adult.
   */
  patientAge: z.number().int().min(0).max(130).nullable().optional(),
  patientSex: z.enum(["male", "female"]).nullable().optional(),
  /**
   * The doctor's own speciality, used to read the terminology they speak —
   * the same abbreviation means different things in different practices. It
   * does NOT narrow what gets recorded: a primary visit takes down everything
   * the patient brought, whichever speciality it belongs to.
   */
  doctorSpeciality: z.string().nullable().optional(),
});

export const PrimaryVisitExtractResponseSchema = z.object({
  /** Only what the doctor actually said. Anything unsaid comes back empty. */
  structuredData: z.record(z.string(), z.string().nullable()),
  diagnosisCandidates: z.array(DiagnosisCandidateSchema).default([]),
  doctorNotes: z.string().nullable().optional(),
});

/** A clinical protocol as the API returns it. */
export const ClinicalProtocolDTOSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  language: z.string(),
  icd10Codes: z.array(z.string()),
  specialty: z.string().nullable().optional(),
  examinationPlan: z.string().nullable().optional(),
  treatmentPlan: z.string().nullable().optional(),
  symptoms: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
});

/** LLM service: POST /v1/protocol-plan. */
export const ProtocolPlanRequestSchema = z.object({
  language: z.nativeEnum(Language),
  diagnosis: z.string().min(1),
  icd10: z.string().nullable().optional(),
  protocolTitle: z.string().min(1),
  examinationSource: z.string().nullable().optional(),
  treatmentSource: z.string().nullable().optional(),
  medicationsSource: z.string().nullable().optional(),
  findings: z.string().optional(),
  systemPrompt: z.string().optional(),
});

export const ProtocolPlanResponseSchema = z.object({
  examinationPlan: z.string(),
  treatmentPlan: z.string(),
});
